import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();

export function useThemeMode() {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }) {
  // Get initial mode from localStorage or default to light
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'light';
  });

  // Save mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#1976d2',
            light: '#42a5f5',
            dark: '#1565c0',
          },
          secondary: {
            main: '#9c27b0',
            light: '#ba68c8',
            dark: '#7b1fa2',
          },
          success: {
            main: '#2e7d32',
            light: '#4caf50',
            dark: '#1b5e20',
          },
          error: {
            main: '#d32f2f',
            light: '#ef5350',
            dark: '#c62828',
          },
          warning: {
            main: '#ed6c02',
            light: '#ff9800',
            dark: '#e65100',
          },
          info: {
            main: '#0288d1',
            light: '#03a9f4',
            dark: '#01579b',
          },
          ...(mode === 'dark'
            ? {
                // Dark mode palette
                background: {
                  default: '#121212',
                  paper: '#1e1e1e',
                },
              }
            : {
                // Light mode palette
                background: {
                  default: '#f5f5f5',
                  paper: '#ffffff',
                },
              }),
        },
        typography: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          h1: {
            fontSize: '2.5rem',
            '@media (max-width:600px)': {
              fontSize: '2rem',
            },
          },
          h2: {
            fontSize: '2rem',
            '@media (max-width:600px)': {
              fontSize: '1.75rem',
            },
          },
          h3: {
            fontSize: '1.75rem',
            '@media (max-width:600px)': {
              fontSize: '1.5rem',
            },
          },
          h4: {
            fontSize: '1.5rem',
            '@media (max-width:600px)': {
              fontSize: '1.25rem',
            },
          },
          h5: {
            fontSize: '1.25rem',
            '@media (max-width:600px)': {
              fontSize: '1.1rem',
            },
          },
          h6: {
            fontSize: '1rem',
            '@media (max-width:600px)': {
              fontSize: '0.9rem',
            },
          },
          body1: {
            fontSize: '1rem',
            '@media (max-width:600px)': {
              fontSize: '0.9rem',
            },
          },
          body2: {
            fontSize: '0.875rem',
            '@media (max-width:600px)': {
              fontSize: '0.8rem',
            },
          },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: 8,
                '@media (max-width:600px)': {
                  padding: '6px 12px',
                  fontSize: '0.875rem',
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                boxShadow: mode === 'dark' 
                  ? '0 2px 8px rgba(0,0,0,0.3)' 
                  : '0 2px 8px rgba(0,0,0,0.1)',
                '@media (max-width:600px)': {
                  padding: 12,
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                '@media (max-width:600px)': {
                  padding: 12,
                  width: '100%',
                  maxWidth: '100%'
                },
                width: '100%',
                maxWidth: '100%'
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 8,
                  '@media (max-width:600px)': {
                    marginBottom: 8,
                  },
                },
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                borderRadius: 6,
                '@media (max-width:600px)': {
                  height: '24px',
                  '& .MuiChip-label': {
                    fontSize: '0.75rem',
                    padding: '0 8px',
                  },
                },
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                borderRadius: 12,
                '@media (max-width:600px)': {
                  margin: 16,
                  width: 'calc(100% - 32px)',
                },
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                borderRadius: 0,
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                borderRadius: 0,
                '@media (max-width:600px)': {
                  width: '100%',
                },
              },
            },
          },
          MuiContainer: {
            styleOverrides: {
              root: {
                '@media (max-width:600px)': {
                  paddingLeft: 12,
                  paddingRight: 12,
                  maxWidth: '100%',
                  overflowX: 'hidden'
                },
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden'
              },
            },
          },
          MuiGrid: {
            styleOverrides: {
              root: {
                '@media (max-width:600px)': {
                  '&.MuiGrid-item': {
                    paddingTop: 8,
                    paddingBottom: 8,
                    maxWidth: '100%',
                    flexBasis: '100%'
                  },
                  '&.MuiGrid-container': {
                    margin: 0,
                    width: '100%'
                  }
                },
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden'
              },
            },
          },
          MuiBox: {
            styleOverrides: {
              root: {
                '@media (max-width:600px)': {
                  maxWidth: '100%',
                  overflowX: 'hidden'
                },
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden'
              },
            },
          },
          MuiTableContainer: {
            styleOverrides: {
              root: {
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden'
              },
            },
          },
          MuiTable: {
            styleOverrides: {
              root: {
                tableLayout: 'fixed',
                width: '100%',
                '& .MuiTableCell-root': {
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  '@media (max-width:600px)': {
                    padding: '8px 4px',
                    fontSize: '0.75rem'
                  }
                }
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                '@media (max-width:600px)': {
                  maxWidth: 'none',
                  overflow: 'visible'
                }
              },
            },
          },
        },
        shape: {
          borderRadius: 8,
        },
        breakpoints: {
          values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
          },
        },
      }),
    [mode]
  );

  const value = {
    mode,
    toggleColorMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

